import { forwardRef } from 'react';
import { Dialog as HeadlessDialog, DialogPanel, DialogTitle } from '@headlessui/react';
import Draggable from 'react-draggable';
import styles from './Dialog.module.scss';
import useKeydown from '../../../hooks/useKeydown';
import { classnames } from '../../../utils';

type Props = {
  title?: string | React.ReactNode;
  type?: 'info' | 'success' | 'danger';
  isOpen: boolean;
  onClose(): void;
  children: React.ReactNode;
  size?: 'small' | 'medium' | 'large' | 'larger' | 'xlarge';
  actions?: React.ReactNode;
  hint?: string | React.ReactNode;
  header?: React.ReactNode;
  blur?: boolean;
  overflowAuto?: boolean;
  onEnterPress?: () => void;
  unmount?: boolean;
  nonDismissable?: boolean; // No close button
  draggable?: boolean;
};

type Ref = HTMLDivElement;

const Dialog = forwardRef<Ref, Props>((props, ref) => {
  const {
    isOpen,
    onClose,
    children,
    actions,
    title,
    size = 'medium',
    type = 'info',
    hint,
    header,
    overflowAuto = true,
    onEnterPress,
    unmount = true,
    nonDismissable,
    draggable
  } = props;

  useKeydown('Enter', onEnterPress);

  return (
    <HeadlessDialog open={isOpen} onClose={onClose} className={styles.wrapper} unmount={unmount}>
      <div className={classnames([styles.backdrop, props.blur && styles.backdropBlur])} aria-hidden="true" />
      <Draggable handle=".dialog-handle">
        <div className={styles.panelWrapper}>
          <DialogPanel className={`${styles.panel} ${styles[size]}`}>
            {draggable ? <div className="dialog-handle"></div> : null}
            <header className={styles[type]}>
              <div>{!nonDismissable ? <button onClick={onClose} className={styles.closeButton}></button> : null}</div>
              {title ? <DialogTitle>{title}</DialogTitle> : null}
            </header>
            {header ? <div className={styles.header}>{header}</div> : null}
            <div className={`${styles.childWrapper} ${!overflowAuto ? styles.noOverflowAuto : ''}`} ref={ref}>
              {children}
            </div>
            {actions ? <div className={styles.actions}>{actions}</div> : null}
            {hint ? <div className={styles.hint}>{hint}</div> : null}
          </DialogPanel>
        </div>
      </Draggable>
    </HeadlessDialog>
  );
});

export default Dialog;
